import { startOfMonth, subDays, subHours } from 'date-fns';
import { DATE_RANGE_TYPES, type DateRangeType } from '@/lib/constants/dateFormats';

/**
 * Date range calculation utilities
 * Provides consistent date range calculations across analytics and filters
 */

export interface DateRangeResult {
    from: Date;
    to: Date;
}

/**
 * Calculates date range for a given range type
 * Used by both analytics and filter components
 */
export function calculateDateRange(type: DateRangeType, start?: Date, end?: Date): DateRangeResult | null {
    if (start && end) {
        return { from: start, to: end };
    }

    const today = new Date();

    switch (type) {
        case DATE_RANGE_TYPES.LAST_24_HOURS:
            return {
                from: subHours(today, 24),
                to: today,
            };
        case DATE_RANGE_TYPES.LAST_7_DAYS:
            return {
                from: subDays(today, 7),
                to: today,
            };
        case DATE_RANGE_TYPES.LAST_14_DAYS:
            return {
                from: subDays(today, 14),
                to: today,
            };
        case DATE_RANGE_TYPES.LAST_30_DAYS:
            return {
                from: subDays(today, 30),
                to: today,
            };
        case DATE_RANGE_TYPES.LAST_90_DAYS:
            return {
                from: subDays(today, 90),
                to: today,
            };
        case DATE_RANGE_TYPES.MONTH_TO_DATE:
            return {
                from: startOfMonth(today),
                to: new Date(today.getTime() + 24 * 60 * 60 * 1000), // Tomorrow
            };
        default:
            return null;
    }
}

/**
 * Preset options for date filters
 * Provides consistent preset options across the application
 */
export interface DateRangePreset {
    label: string;
    value: string;
    rangeType: DateRangeType;
    getDateRange: () => DateRangeResult;
}

/**
 * Standard date range presets used across analytics and filters
 */
export const DATE_RANGE_PRESETS: DateRangePreset[] = [
    {
        label: 'Last 24 hours',
        value: '24h',
        rangeType: DATE_RANGE_TYPES.LAST_24_HOURS,
        getDateRange: () => calculateDateRange(DATE_RANGE_TYPES.LAST_24_HOURS) || { from: new Date(), to: new Date() },
    },
    {
        label: 'Last 7 days',
        value: '7d',
        rangeType: DATE_RANGE_TYPES.LAST_7_DAYS,
        getDateRange: () => calculateDateRange(DATE_RANGE_TYPES.LAST_7_DAYS) || { from: new Date(), to: new Date() },
    },
    {
        label: 'Last 14 days',
        value: '14d',
        rangeType: DATE_RANGE_TYPES.LAST_14_DAYS,
        getDateRange: () => calculateDateRange(DATE_RANGE_TYPES.LAST_14_DAYS) || { from: new Date(), to: new Date() },
    },
    {
        label: 'Last 30 days',
        value: '30d',
        rangeType: DATE_RANGE_TYPES.LAST_30_DAYS,
        getDateRange: () => calculateDateRange(DATE_RANGE_TYPES.LAST_30_DAYS) || { from: new Date(), to: new Date() },
    },
    {
        label: 'Last 90 days',
        value: '90d',
        rangeType: DATE_RANGE_TYPES.LAST_90_DAYS,
        getDateRange: () => calculateDateRange(DATE_RANGE_TYPES.LAST_90_DAYS) || { from: new Date(), to: new Date() },
    },
];

/**
 * Finds a preset that matches the given date range
 * Used to detect if a custom range matches any preset
 */
export function findMatchingPreset(
    from: Date,
    to: Date,
    presets: DateRangePreset[] = DATE_RANGE_PRESETS
): DateRangePreset | null {
    // Normalize dates to start of day for comparison (ignore time differences)
    const normalizeDate = (date: Date) => {
        const normalized = new Date(date);
        normalized.setHours(0, 0, 0, 0);
        return normalized;
    };

    const normalizedFrom = normalizeDate(from);
    const normalizedTo = normalizeDate(to);

    // Check each preset to see if the selected dates match
    for (const preset of presets) {
        const { from: presetFrom, to: presetTo } = preset.getDateRange();
        const normalizedPresetFrom = normalizeDate(presetFrom);
        const normalizedPresetTo = normalizeDate(presetTo);

        // Check if the dates match the preset pattern exactly
        const fromMatches = normalizedFrom.getTime() === normalizedPresetFrom.getTime();
        const toMatches = normalizedTo.getTime() === normalizedPresetTo.getTime();

        if (fromMatches && toMatches) {
            return preset;
        }
    }

    return null;
}
